import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import { AreaLayer } from './AreaLayer'
import type { Area } from '@/lib/types/area'
import type { Table, TableState, TableCombination } from '@/lib/types/table'
import type { Booking, BookingStatus } from '@/lib/types/booking'
import { useMemo, useState } from 'react'
import { parseISO, addMinutes, isBefore, isAfter } from 'date-fns'
import { getShape } from './TableShapeRenderer'

export type NearbyInfo = { names: string[]; glow: 'green' | 'red' }

interface FloorPlanCanvasProps {
  areas: Area[]
  bookings: Booking[]
  combinations: TableCombination[]
  onDragEnd: (event: DragEndEvent) => void
  onAreaClick: (areaId: string) => void
  onBreakCombo?: (documentId: string) => void
  posOrdersByTableDocumentId?: Map<string, 'none' | 'open' | 'in_progress' | 'paid'>
  onPOSTableClick?: (tableDocumentId: string) => void
}

function getTableStateAtTime(
  table: Table,
  bookings: Booking[],
  timelineDate: string,
  timelineHour: string | null,
): { state: TableState; guestName?: string } {
  if (!table.bookable) return { state: 'not_bookable' }

  const tableBookings = bookings.filter((b) =>
    b.bookingTables?.some((bt) => bt.table?.id === table.id),
  )

  // All day view: return most significant status across all bookings for the day
  if (timelineHour === null) {
    if (tableBookings.some((b) => ['arrived', 'seated'].includes(b.status))) {
      const b = tableBookings.find((b) => ['arrived', 'seated'].includes(b.status))!
      return { state: 'occupied', guestName: b.guest?.name }
    }
    if (tableBookings.some((b) => b.status === 'approved')) {
      const b = tableBookings.find((b) => b.status === 'approved')!
      return { state: 'reserved', guestName: b.guest?.name }
    }
    if (tableBookings.some((b) => b.status === 'waitlist')) return { state: 'waitlist' }
    if (tableBookings.some((b) => b.status === 'request')) {
      const b = tableBookings.find((b) => b.status === 'request')!
      return { state: 'reserved', guestName: b.guest?.name }
    }
    return { state: 'available' }
  }

  const viewTime = new Date(`${timelineDate}T${timelineHour}:00`)

  // Always build start from b.date + b.time (local, no tz offset).
  // Falling back to b.dateTime (UTC ISO) would shift the time by the user's timezone.
  const isoStart = (b: Booking) =>
    b.date && b.time ? `${b.date}T${b.time}:00` : b.dateTime ?? null

  // Derive end from duration so it stays in local time to match the start.
  const getEnd = (b: Booking, start: Date) =>
    addMinutes(start, b.duration || 90)

  for (const b of tableBookings) {
    const iso = isoStart(b)
    if (!iso) continue
    const start = parseISO(iso)
    const end = getEnd(b, start)

    const activeStatuses: BookingStatus[] = ['arrived', 'seated']
    if (activeStatuses.includes(b.status) && isBefore(viewTime, end) && isAfter(viewTime, start)) {
      return { state: 'occupied', guestName: b.guest?.name }
    }
  }

  for (const b of tableBookings) {
    if (b.status === 'waitlist') return { state: 'waitlist' }
  }

  for (const b of tableBookings) {
    const iso = isoStart(b)
    if (!iso) continue
    if (b.status === 'approved') {
      const start = parseISO(iso)
      const windowStart = addMinutes(start, -30)
      if (isAfter(viewTime, windowStart) && isBefore(viewTime, start)) {
        return { state: 'reserved', guestName: b.guest?.name }
      }
    }
  }

  // Check if the view time falls within any active booking window (request/approved)
  for (const b of tableBookings) {
    const iso = isoStart(b)
    if (!iso) continue
    if (['request', 'approved'].includes(b.status)) {
      const start = parseISO(iso)
      const end = getEnd(b, start)
      if (!isBefore(viewTime, start) && isBefore(viewTime, end)) {
        return { state: 'reserved', guestName: b.guest?.name }
      }
    }
  }

  return { state: 'available' }
}


function computeNearbyGroups(
  areas: Area[],
  positions: Record<string, { x: number; y: number }>,
  bookedTableIds: Set<number>,
  combinations: TableCombination[],
  isDragging: boolean,
): Map<string, NearbyInfo> {
  const result = new Map<string, NearbyInfo>()

  for (const area of areas) {
    const tables = area.tables ?? []
    for (let i = 0; i < tables.length; i++) {
      for (let j = i + 1; j < tables.length; j++) {
        const a = tables[i]
        const b = tables[j]
        const posA = positions[String(a.id)]
        const posB = positions[String(b.id)]
        if (!posA || !posB) continue

        if (bookedTableIds.has(a.id) || bookedTableIds.has(b.id)) continue

        const shapeA = getShape(a.seatsMax, a.shape)
        const shapeB = getShape(b.seatsMax, b.shape)
        const adjacentOrOverlap =
          posA.x <= posB.x + shapeB.width &&
          posA.x + shapeA.width >= posB.x &&
          posA.y <= posB.y + shapeB.height &&
          posA.y + shapeA.height >= posB.y

        if (!adjacentOrOverlap) continue

        const aCombinable = a.combinable !== false
        const bCombinable = b.combinable !== false

        if (aCombinable && bCombinable) {
          // When not dragging: only show green glow if a combination record already exists.
          // When dragging: always show green glow as real-time drop preview.
          const hasCombination = isDragging || combinations.some(
            (c) => c.tables?.some((t) => t.id === a.id) && c.tables?.some((t) => t.id === b.id),
          )
          if (!hasCombination) continue

          // Both combinable → both get green glow
          for (const [id, selfName, otherName] of [
            [String(a.id), a.name, b.name],
            [String(b.id), b.name, a.name],
          ] as [string, string, string][]) {
            const existing = result.get(id)
            if (existing) {
              if (!existing.names.includes(otherName)) existing.names.push(otherName)
            } else {
              result.set(id, { names: [selfName, otherName], glow: 'green' })
            }
          }
        } else {
          // At least one is not combinable → only the non-combinable table(s) get red glow
          if (!aCombinable) {
            const existing = result.get(String(a.id))
            if (existing) {
              if (!existing.names.includes(b.name)) existing.names.push(b.name)
            } else {
              result.set(String(a.id), { names: [a.name, b.name], glow: 'red' })
            }
          }
          if (!bCombinable) {
            const existing = result.get(String(b.id))
            if (existing) {
              if (!existing.names.includes(a.name)) existing.names.push(a.name)
            } else {
              result.set(String(b.id), { names: [b.name, a.name], glow: 'red' })
            }
          }
        }
      }
    }
  }

  return result
}

export function FloorPlanCanvas({ areas, bookings, combinations, onDragEnd, onAreaClick, onBreakCombo, posOrdersByTableDocumentId, onPOSTableClick }: FloorPlanCanvasProps) {
  const { zoom, positions, timelineDate, timelineHour, mode } = useFloorPlanStore()
  const [dragState, setDragState] = useState<{ id: string; delta: { x: number; y: number } } | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  const allTables = useMemo(() => areas.flatMap((a) => a.tables ?? []), [areas])

  const tableStates = useMemo(() => {
    const map = new Map<number, { state: TableState; guestName?: string }>()
    for (const t of allTables) {
      map.set(t.id, getTableStateAtTime(t, bookings, timelineDate, timelineHour))
    }
    return map
  }, [allTables, bookings, timelineDate, timelineHour])

  // In live mode: suppress proximity glow for booked tables (they're in use).
  // In layout mode: always show glow — we're editing arrangements, not managing bookings.
  const bookedTableIds = useMemo(() => {
    if (mode === 'layout') return new Set<number>()
    const ids = new Set<number>()
    for (const [id, s] of tableStates) {
      if (s.state === 'occupied' || s.state === 'reserved') ids.add(id)
    }
    return ids
  }, [mode, tableStates])

  // Override the dragging table's stored position with its live drag position for real-time proximity
  const livePositions = useMemo(() => {
    if (!dragState) return positions
    const stored = positions[dragState.id] ?? { x: 0, y: 0 }
    return {
      ...positions,
      [dragState.id]: {
        x: stored.x + dragState.delta.x / zoom,
        y: stored.y + dragState.delta.y / zoom,
      },
    }
  }, [positions, dragState, zoom])

  const nearbyGroups = useMemo(
    () => computeNearbyGroups(areas, livePositions, bookedTableIds, combinations, dragState !== null),
    [areas, livePositions, bookedTableIds, combinations, dragState],
  )

  const handleDragEnd = (e: DragEndEvent) => {
    setDragState(null)
    onDragEnd(e)
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e: DragStartEvent) =>
        setDragState({ id: String(e.active.id), delta: { x: 0, y: 0 } })
      }
      onDragMove={(e: DragMoveEvent) =>
        setDragState((prev) =>
          prev ? { ...prev, delta: { x: e.delta.x, y: e.delta.y } } : null,
        )
      }
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragState(null)}
    >
      <div
        className="relative flex-1 overflow-auto bg-[var(--color-bg-base)]"
        style={{
          backgroundImage: 'radial-gradient(circle, var(--color-border-subtle) 1px, transparent 1px)',
          backgroundSize: `${16 * zoom}px ${16 * zoom}px`,
        }}
      >
        <div
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
          }}
        >
          <AreaLayer
            areas={areas}
            tableStates={tableStates}
            nearbyGroups={nearbyGroups}
            onAreaClick={onAreaClick}
            combinations={combinations}
            onBreakCombo={onBreakCombo}
            posOrdersByTableDocumentId={posOrdersByTableDocumentId}
            onPOSTableClick={onPOSTableClick}
          />
        </div>
      </div>
    </DndContext>
  )
}
